// features/home/HomeHero.tsx — dismissible welcome band shown on the Home feed.
//
// Compact introduction and secondary Explore link in the page flow.
// A dismiss button sets localStorage['truxsocial:homeHeroDismissed'] and hides
// the band. On mount, if the key is already set the component renders null.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { IconButton } from '@/ui';
import { ConsolePanel } from '@/ui/ConsolePanel/ConsolePanel';
import { media } from '@/theme/media';
import landscape from '@/features/landing/assets/hero-landscape-v1.png';

const DISMISS_KEY = 'truxsocial:homeHeroDismissed';

// ── Styled pieces ─────────────────────────────────────────────────────────────

const Wrap = styled(ConsolePanel).attrs({ as: 'section' })`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px;
  min-width: 0;
  position: relative;
  min-height: 158px;
  background:
    linear-gradient(
      90deg,
      rgba(14, 11, 38, 0.96),
      rgba(25, 15, 56, 0.65) 52%,
      rgba(25, 15, 56, 0.12)
    ),
    url(${landscape}) center 54% / cover;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};

  ${media.mobile`padding: 16px;`}
`;

const DismissSlot = styled.div`
  margin-left: auto;
  align-self: flex-start;
  flex-shrink: 0;
`;

const Introduction = styled.div`
  flex: 1 1 260px;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const Headline = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: clamp(22px, 2vw, 30px);
  font-weight: 600;
  color: #fff;
  line-height: 1.2;
`;

const HeadlinePart = styled.span`
  display: block;
  color: inherit;
`;

const Subcopy = styled.p`
  margin: 4px 0 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #e2ddec;
`;

const ExploreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.xs};
  padding: 8px 12px;
  min-height: 44px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid #9c88b4;
  cursor: pointer;
  text-decoration: none;
  background: rgba(18, 14, 38, 0.8);
  color: #fff;
  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

export function HomeHero() {
  // Lazy initializer reads localStorage synchronously on first mount —
  // no need for useEffect, avoids the cascading-setState lint rule.
  const [dismissed, setDismissed] = useState<boolean>(
    () => localStorage.getItem(DISMISS_KEY) === '1',
  );

  function handleDismiss() {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }

  if (dismissed) return null;

  return (
    <Wrap aria-label="Welcome to TruXSocial">
      <Introduction>
        <Headline>
          Speak Freely. <HeadlinePart>Live Authentically.</HeadlinePart>
        </Headline>

        <Subcopy>Real people. Real conversations. A more open tomorrow.</Subcopy>
      </Introduction>
      <ExploreLink to="/explore">Explore TruXSocial</ExploreLink>
      <DismissSlot>
        <IconButton icon="close" label="Dismiss" onClick={handleDismiss} />
      </DismissSlot>
    </Wrap>
  );
}
